"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { ArrowRight } from "lucide-react";

const heroImages = [
  {
    src: "https://images.unsplash.com/photo-1651760937033-8ccab2727cef?w=600",
    alt: "精选产品展示",
    className: "col-span-1 row-span-2",
  },
  {
    src: "https://images.unsplash.com/photo-1606741965509-717b9fdd6549?w=600",
    alt: "耳机产品",
    className: "col-span-1 row-span-1",
  },
  {
    src: "https://images.unsplash.com/photo-1758887952896-8491d393afe2?w=600",
    alt: "智能手表",
    className: "col-span-1 row-span-1",
  },
];

export default function Hero() {
  return (
    <section className="relative min-h-[90vh] w-full overflow-hidden bg-[#F8FAFC]">
      <div className="mx-auto flex min-h-[90vh] max-w-[1600px] flex-col lg:flex-row">
        {/* 左侧内容区 - 40% */}
        <div className="flex w-full flex-col justify-center px-6 py-16 sm:px-8 lg:w-[40%] lg:px-12 lg:py-0 xl:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="space-y-8"
          >
            {/* 品牌标识 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex items-center gap-3"
            >
              <div className="h-[2px] w-8 bg-[#3B82F6]" />
              <span className="text-sm font-medium tracking-[0.2em] text-[#94A3B8] uppercase">
                AURA 精选
              </span>
            </motion.div>

            {/* 主标题 */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="font-serif text-4xl font-light leading-[1.1] tracking-[-0.02em] text-[#1A1A1A] sm:text-5xl lg:text-[56px]"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              发现生活中的
              <br />
              <span className="text-[#3B82F6]">精致之美</span>
            </motion.h1>

            {/* 副标题 */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="max-w-md text-base leading-relaxed text-[#64748B]"
            >
              甄选全球优质好物，以极简设计理念，为您呈现品质与美学并存的精选产品。每一件都是生活的艺术品。
            </motion.p>

            {/* CTA按钮 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="flex flex-wrap items-center gap-4 pt-4"
            >
              <button className="group flex items-center gap-2 rounded-lg bg-[#3B82F6] px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:bg-[#1E40AF]">
                探索产品
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button className="flex items-center gap-2 rounded-lg border border-[#1A1A1A] bg-transparent px-8 py-4 text-sm font-medium text-[#1A1A1A] transition-all duration-300 hover:bg-[#F1F5F9]">
                了解更多
              </button>
            </motion.div>

            {/* 数据展示 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="flex items-center gap-8 pt-8 border-t border-[#E2E8F0]"
            >
              <div>
                <div className="text-2xl font-semibold text-[#3B82F6]" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                  2000+
                </div>
                <div className="text-xs text-[#94A3B8] tracking-wide">精选产品</div>
              </div>
              <div className="h-8 w-[1px] bg-[#E2E8F0]" />
              <div>
                <div className="text-2xl font-semibold text-[#3B82F6]" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                  50K+
                </div>
                <div className="text-xs text-[#94A3B8] tracking-wide">满意客户</div>
              </div>
              <div className="h-8 w-[1px] bg-[#E2E8F0]" />
              <div>
                <div className="text-2xl font-semibold text-[#3B82F6]" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                  4.9
                </div>
                <div className="text-xs text-[#94A3B8] tracking-wide">用户评分</div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* 右侧图片区 - 60% */}
        <div className="relative w-full lg:w-[60%]">
          <div className="relative h-[50vh] w-full lg:h-full">
            {/* 背景装饰 */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#F8FAFC] via-transparent to-[#F8FAFC]/50" />
            
            {/* 瀑布流图片网格 */}
            <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-4 p-4 lg:p-8">
              {/* 大图 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="relative col-span-1 row-span-2 overflow-hidden rounded-2xl"
              >
                <Image
                  src={heroImages[0].src}
                  alt={heroImages[0].alt}
                  fill
                  className="object-cover transition-transform duration-700 hover:scale-105"
                  priority
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </motion.div>

              {/* 右上小图 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="relative col-span-1 row-span-1 overflow-hidden rounded-2xl"
              >
                <Image
                  src={heroImages[1].src}
                  alt={heroImages[1].alt}
                  fill
                  className="object-cover transition-transform duration-700 hover:scale-105"
                />
              </motion.div>

              {/* 右下小图 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="relative col-span-1 row-span-1 overflow-hidden rounded-2xl"
              >
                <Image
                  src={heroImages[2].src}
                  alt={heroImages[2].alt}
                  fill
                  className="object-cover transition-transform duration-700 hover:scale-105"
                />
              </motion.div>
            </div>

            {/* 装饰元素 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.1 }}
              transition={{ delay: 1, duration: 1 }}
              className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#3B82F6] blur-3xl"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.08 }}
              transition={{ delay: 1.2, duration: 1 }}
              className="pointer-events-none absolute -bottom-20 -left-20 h-48 w-48 rounded-full bg-[#60A5FA] blur-3xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
